import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, ExternalLink } from "lucide-react";
import { SiGooglechrome } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { useLocation } from "wouter";
import { useCookieConsent } from "./cookie-consent-provider";

const STORAGE_KEY_CHROME = "abmark.banner.chrome.dismissed";
const STORAGE_KEY_PWA = "abmark.banner.pwa.dismissed";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isChromeBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const isChromium = /Chrome|CriOS/.test(ua);
  const isOther = /Edg\/|EdgiOS|OPR\/|FxiOS|SamsungBrowser|YaBrowser|UCBrowser/.test(ua);
  return isChromium && !isOther;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mql = window.matchMedia?.("(display-mode: standalone)");
  return Boolean(mql?.matches) || (window.navigator as any).standalone === true;
}

const bannerVariants = {
  hidden: { y: -80, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: "spring" as const, stiffness: 320, damping: 32 },
  },
  exit: { y: -80, opacity: 0, transition: { duration: 0.2 } },
};

export function PwaPrompt() {
  const [location] = useLocation();
  const { showBanner } = useCookieConsent();
  const [installEvent, setInstallEvent] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showChromeHint, setShowChromeHint] = useState(false);
  const [showInstallHint, setShowInstallHint] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;

    const chromeDismissed =
      localStorage.getItem(STORAGE_KEY_CHROME) === "1";
    const pwaDismissed = localStorage.getItem(STORAGE_KEY_PWA) === "1";

    if (isChromeBrowser()) {
      if (pwaDismissed) return;

      const handler = (e: Event) => {
        e.preventDefault();
        setInstallEvent(e as BeforeInstallPromptEvent);
        setShowInstallHint(true);
      };
      const installedHandler = () => {
        setShowInstallHint(false);
        setInstallEvent(null);
        localStorage.setItem(STORAGE_KEY_PWA, "1");
      };

      window.addEventListener("beforeinstallprompt", handler);
      window.addEventListener("appinstalled", installedHandler);

      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
        window.removeEventListener("appinstalled", installedHandler);
      };
    }

    if (!chromeDismissed) {
      const t = window.setTimeout(() => setShowChromeHint(true), 1800);
      return () => window.clearTimeout(t);
    }
  }, []);

  const handleInstall = async () => {
    if (!installEvent) {
      setShowInstallHint(false);
      return;
    }
    try {
      await installEvent.prompt();
      const choice = await installEvent.userChoice;
      setShowInstallHint(false);
      setInstallEvent(null);
      if (choice.outcome === "dismissed") {
        localStorage.setItem(STORAGE_KEY_PWA, "1");
      }
    } catch {
      setShowInstallHint(false);
    }
  };

  const dismissChrome = () => {
    localStorage.setItem(STORAGE_KEY_CHROME, "1");
    setShowChromeHint(false);
  };

  const dismissInstall = () => {
    localStorage.setItem(STORAGE_KEY_PWA, "1");
    setShowInstallHint(false);
  };

  // Do not interrupt the consent choice or public intake forms.
  const visible = !showBanner && location === "/";
  return visible ? (
    <AnimatePresence>
      {showChromeHint && (
        <motion.div
          key="chrome-hint"
          variants={bannerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
           className="relative z-40 mx-auto w-full max-w-xl px-3 py-2"
          data-testid="banner-chrome-hint"
          role="status"
          aria-live="polite"
        >
           <div className="site-card shadow-xl overflow-hidden">
            <div className="px-4 py-3 md:px-5 md:py-3.5 flex items-center gap-3">
              <div className="rounded-full bg-primary/5 p-2 border border-primary/10 flex-shrink-0 hidden sm:flex items-center justify-center">
                <SiGooglechrome className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm text-foreground font-medium leading-snug">
                  Esperienza ottimale con Google Chrome
                </p>
                 <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                  Per la migliore esperienza ti consigliamo di visitare il sito da Chrome.
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button
                  asChild
                  size="sm"
                  data-testid="button-download-chrome"
                >
                  <a
                    href="https://www.google.com/chrome/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="hidden sm:inline">Scarica Chrome</span>
                    <span className="sm:hidden">Chrome</span>
                    <ExternalLink className="ml-1 h-3.5 w-3.5" />
                  </a>
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={dismissChrome}
                  aria-label="Chiudi avviso Chrome"
                  data-testid="button-dismiss-chrome-hint"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {showInstallHint && (
        <motion.div
          key="install-hint"
          variants={bannerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
           className="relative z-40 mx-auto w-full max-w-xl px-3 py-2"
          data-testid="banner-install-hint"
          role="status"
          aria-live="polite"
        >
           <div className="site-card shadow-xl overflow-hidden">
            <div className="px-4 py-3 md:px-5 md:py-3.5 flex items-center gap-3">
              <div className="rounded-full bg-primary/10 p-2 border border-primary/20 flex-shrink-0 hidden sm:flex items-center justify-center">
                <Download className="h-4 w-4 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm text-foreground font-medium leading-snug">
                  Installa AB MARK come app
                </p>
                 <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                  Accesso rapido dal desktop o dalla schermata Home, anche offline.
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Button
                  size="sm"
                  onClick={handleInstall}
                  data-testid="button-install-pwa"
                >
                  <Download className="mr-1 h-3.5 w-3.5" />
                  Installa
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={dismissInstall}
                  aria-label="Chiudi avviso installazione"
                  data-testid="button-dismiss-install-hint"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  ) : null;
}
